import { BILLING_TYPE_LABELS, formatRate } from '@pulsepoint/shared';
import type { Session } from '../../api/sessions';
import { placeOf } from '../../lib/place';

/** Who charged, in words: the driver and their card, a guest, a start from the app, or an unknown card. */
export function whoCharged(s: Pick<Session, 'driverName' | 'cardLabel' | 'idTag' | 'billingType' | 'receiptNumber'>): { title: string; sub: string } {
  const tag = s.idTag ?? '';
  if (s.driverName) {
    const sub = s.cardLabel ? `${s.cardLabel} · ${tag}` : /^(DIRECT|Remote_)/i.test(tag) ? 'Plug and charge' : tag.startsWith('ADM-') ? 'Started from Pulsepoint' : tag;
    return { title: s.driverName, sub };
  }
  if (s.billingType === 'public') return { title: 'Public charging guest', sub: s.receiptNumber ?? 'Paid by card' };
  if (tag.startsWith('ADM-')) return { title: 'Started from Pulsepoint', sub: 'No card' };
  if (/^(DIRECT|Remote_)/i.test(tag)) return { title: 'Plug and charge', sub: 'No driver chosen' };
  return { title: 'Unknown card', sub: tag };
}

/** "Infinity EV Pty Ltd" or "Richmond office · Bay 2". */
export const whereCharged = (s: Session) => [placeOf(s), s.bay ? `Bay ${s.bay}` : null].filter(Boolean).join(' · ');

/** Who pays: the organisation (by name), the guest's card (with the amount), or nobody. */
export function billedTo(s: Pick<Session, 'billingType' | 'organisationName' | 'paidCents'>): string {
  if (s.billingType === 'organisation') return s.organisationName ?? BILLING_TYPE_LABELS.organisation;
  if (s.billingType === 'public') return s.paidCents != null ? `Card, ${formatRate(s.paidCents / 100)}` : BILLING_TYPE_LABELS.public;
  return BILLING_TYPE_LABELS.unbilled;
}

/** "45 min", "2 h 05 min". */
export function duration(sec: number): string {
  const m = Math.max(0, Math.round(sec / 60));
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')} min`;
}

export const kwhOf = (wh: number, dp = 2) => (wh / 1000).toLocaleString('en-AU', { minimumFractionDigits: dp, maximumFractionDigits: dp });

/** Periods for the date filter, as [from, to) in the browser's time zone. */
export const PERIODS = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7 days' },
  { value: 'month', label: 'This month' },
  { value: 'lastMonth', label: 'Last month' },
  { value: '3m', label: 'Last 3 months' },
  { value: 'year', label: 'This year' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Choose dates' },
] as const;
export type Period = (typeof PERIODS)[number]['value'];

export function periodRange(period: Period, custom: { from: string; to: string }, now = new Date()): { from?: string; to?: string } {
  const day = (y: number, m: number, d: number) => new Date(y, m, d);
  const y = now.getFullYear();
  const m = now.getMonth();
  const today = day(y, m, now.getDate());
  const iso = (d: Date) => d.toISOString();
  switch (period) {
    case 'today':
      return { from: iso(today) };
    case '7d':
      return { from: iso(day(y, m, now.getDate() - 6)) };
    case 'month':
      return { from: iso(day(y, m, 1)) };
    case 'lastMonth':
      return { from: iso(day(y, m - 1, 1)), to: iso(day(y, m, 1)) };
    case '3m':
      return { from: iso(day(y, m - 2, 1)) };
    case 'year':
      return { from: iso(day(y, 0, 1)) };
    case 'custom': {
      // Whole days: from the start of the first to the end of the last.
      const parse = (s: string) => (/^\d{4}-\d{2}-\d{2}$/.test(s) ? s.split('-').map(Number) : null);
      const f = parse(custom.from);
      const t = parse(custom.to);
      return { from: f ? iso(day(f[0]!, f[1]! - 1, f[2]!)) : undefined, to: t ? iso(day(t[0]!, t[1]! - 1, t[2]! + 1)) : undefined };
    }
    default:
      return {};
  }
}
